/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import com.ionspin.kotlin.crypto.box.Box
import com.ionspin.kotlin.crypto.box.BoxCorruptedOrTamperedDataException
import com.ionspin.kotlin.crypto.box.crypto_box_NONCEBYTES
import kotlinx.serialization.Serializable
import net.sergeych.bipack.BipackDecoder
import net.sergeych.bipack.BipackEncoder
import net.sergeych.crypto2.Asymmetric.Message
import net.sergeych.crypto2.Asymmetric.generateKeys

/**
 * Public-key encryption implementation tools.
 *
 * ## How to
 *
 * - [DecryptingSecretKey.new] to create a secret key that includes [DecryptingSecretKey.publicKey].
 *   [generateKeys] also makes the pair.
 * - [EncryptingPublicKey] provides encryption, anonymous or authenticated.
 * - [DecryptingSecretKey] provides authenticated decryption of what [EncryptingPublicKey] was encrypted with.
 * - [Message] is a serializable container with all necessary data to decrypt public-key encrypted data it.
 *
 * __Algorithms:__
 *
 * - Key exchange: X25519.
 * - Encryption: XSalsa20
 * - Authentication: Poly1305
 */
object Asymmetric {

    /**
     * Encrypted message holder.
     *
     * Do not instantiate it directly, use [EncryptingPublicKey.encryptMessage], [EncryptingPublicKey.encryptAnonymousMessage], etc.
     * instead. Also [DecryptingSecretKey.decrypt] can be used to decrypt it same as [decrypt] or [decryptWithSenderKey].
     *
     * To successfully decrypt the message, it is necessary to know a sender public key, and non-secret nonce.
     * This class carries all this information; serialize and pass it to the recipient.
     */
    @Serializable
    class Message(
        private val nonce: UByteArray,
        private val encryptedMessage: UByteArray,
        val senderPublicKey: EncryptingPublicKey,
    ) {
        /**
         * Decrypt the message, same as [DecryptingSecretKey.decrypt]
         */
        fun decrypt(recipientKey: DecryptingSecretKey): UByteArray {
            return decryptWithSenderKey(senderPublicKey, recipientKey)
        }

        /**
         * Decrypt a message which is not include sender's public key (which should somehow be
         * known to the recipient). Use it if [senderPublicKey] is null.
         */
        fun decryptWithSenderKey(senderKey: EncryptingPublicKey, recipientKey: DecryptingSecretKey): UByteArray {
            return try {
                WithFill.decode(
                    Box.openEasy(encryptedMessage, nonce, senderKey.keyBytes, recipientKey.keyBytes)
                )
            } catch (_: BoxCorruptedOrTamperedDataException) {
                throw DecryptionFailedException()
            }
        }

        val encoded: UByteArray by lazy { BipackEncoder.encode(this).toUByteArray() }

        companion object {
            fun decode(packed: UByteArray): Message = try {
                BipackDecoder.decode(packed)
            }
            catch(x: Exception) {
                throw DecryptionFailedException("can't decode message structure",x)
            }
        }

    }

    /**
     * Encrypt the [plainData] using [from] sender for [recipient] public key. Note that to decrypt it
     * the [DecryptingSecretKey] that corresponds to the [recipient] public key is needed, Sender can't decrypt the message!
     *
     * The authenticated encryption is used, is the message _is successfully decrypted_, it also means that
     * it was signed by the sender, whose public key is known at the decryption time.
     *
     * When it is important not to provide senders' key, use [EncryptingPublicKey.encryptAnonymousMessage].
     *
     * @param from the senders' secret key.
     * @param recipient the recipients' public key.
     * @param plainData data to encrypt
     */
    internal fun createMessage(
        from: DecryptingSecretKey, recipient: EncryptingPublicKey, plainData: UByteArray,
        nonce: UByteArray = randomNonce(),
    ): Message {
        return Message(
            nonce,
            Box.easy(plainData, nonce, recipient.keyBytes, from.keyBytes),
            from.publicKey
        )
    }


    private fun randomNonce(): UByteArray = randomUBytes(crypto_box_NONCEBYTES)

    fun generateKeys() = DecryptingSecretKey.generateKeys()
    fun newSecretKey() = DecryptingSecretKey.new()

    val nonceBytesLength = crypto_box_NONCEBYTES

}

/**
 * Shortcut type: a pair of sender secret key and recipient private key could be used so
 * simplify such interfaces
 */
typealias AsymmetricEncryptionPair = Pair<DecryptingSecretKey?, EncryptingPublicKey>
